import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axiosInstance from "../../utils/axiosInstance";
import moment from "moment";
import PageHeader from "../../Components/PageHeader";

function DoctorSlotsPage() {
  const { doctorId, date } = useParams();
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSlotIndex, setExpandedSlotIndex] = useState(null);
  const [doctorName, setDoctorName] = useState("");
  const [departmentName, setDepartmentName] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDoctorInfo = async () => {
      try {
        const res = await axiosInstance.get(`/doctors/details/${doctorId}`);
        console.log("Fetched doctor info:", res.data);
        setDoctorName(res.data.name || "Unknown Doctor");
        setDepartmentName(res.data.departmentName || "");
      } catch (err) {
        console.error("Error fetching doctor info:", err);
        setDoctorName("Doctor");
        setDepartmentName("");
      }
    };

    const fetchSlots = async () => {
      try {
        const res = await axiosInstance.get(
          `/doctor/${doctorId}/schedules/${date}/slots`
        );
        console.log("Fetched slots:", res.data);
        setSlots(res.data);
      } catch (err) {
        console.error("Error fetching slots:", err);
        setSlots([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDoctorInfo();
    fetchSlots();
  }, [doctorId, date]);

  const handleSlotClick = (slot) => {
    const params = new URLSearchParams({
      doctorScheduleId: slot.doctorScheduleId,
      date,
      type: slot.type,
      token: slot.tokenNumber,
      startTime: slot.startTime,
      endTime: slot.endTime,
      doctorName,
      departmentName,
    });

    navigate(`/dashboard/book-slot?${params.toString()}`);
  };

  const handleReserveSlot = async (slot) => {
    try {
      await axiosInstance.post("/bookings/reserveSlot", {
        doctorScheduleId: slot.doctorScheduleId,
        bookingDate: date,
        tokenNumber: slot.tokenNumber,
        startTime: slot.startTime,
        endTime: slot.endTime,
      });
      alert("Slot reserved successfully.");
      window.location.reload(); // or re-fetch slots
    } catch (err) {
      console.error("Error reserving slot:", err);
      alert("Failed to reserve slot.");
    }
  };

  const handleUnreserveSlot = async (slot) => {
    try {
      if (!slot.bookingId) {
        alert("Booking ID not found");
        return;
      }
      await axiosInstance.delete(`/bookings/${slot.bookingId}/unreserve`);
      alert("Slot unreserved successfully.");
      window.location.reload(); // or re-fetch slots
    } catch (err) {
      console.error("Error unreserving slot:", err);
      alert("Failed to unreserve slot.");
    }
  };

  const toggleExpand = (idx) => {
    setExpandedSlotIndex(expandedSlotIndex === idx ? null : idx);
  };

  return (
    <div className="p-2 sm:p-4 md:p-6">
      {/* Page Header */}
      <PageHeader
        title="Available Slots"
        subtitle={
          !loading && doctorName ? (
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mt-2">
              <div className="flex items-center gap-2 text-blue-600 font-semibold">
                <svg
                  className="w-4 h-4 sm:w-5 sm:h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
                <span className="text-base sm:text-lg">{doctorName}</span>
              </div>
              <div className="hidden sm:block text-gray-400">•</div>
              <div className="flex items-center gap-2 text-gray-700">
                <svg
                  className="w-4 h-4 sm:w-5 sm:h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <span className="font-medium text-base sm:text-lg">
                  {moment(date).format("dddd, MMMM DD, YYYY")}
                </span>
              </div>
            </div>
          ) : (
            "Loading information..."
          )
        }
        showBackButton={true}
      />

      <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4 md:p-6">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="ml-3 text-gray-600">Loading slots...</p>
          </div>
        ) : slots.length === 0 ? (
          <div className="text-center py-8">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <p className="mt-2 text-gray-500 italic">
              No slots available for this date.
            </p>
          </div>
        ) : (
          <>
            {/* Mobile: Show count and filter summary */}
            <div className="mb-4 pb-3 border-b border-gray-200 flex flex-wrap gap-2 items-center justify-between">
              <div className="text-sm text-gray-600">
                <span className="font-semibold">{slots.length}</span> total
                slots
              </div>
              <div className="flex gap-2 text-xs flex-wrap">
                <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full font-medium">
                  {
                    slots.filter((s) => !s.blocked && !s.booked && !s.reserved)
                      .length
                  }{" "}
                  Available
                </span>
                <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full font-medium">
                  {slots.filter((s) => s.booked).length} Booked
                </span>
                <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full font-medium">
                  {slots.filter((s) => s.reserved).length} Reserved
                </span>
                <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full font-medium">
                  {slots.filter((s) => s.blocked).length} Blocked
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {slots.map((slot, idx) => {
                // Determine slot status and styling
                const isBlocked = slot.blocked;
                const isBooked = slot.booked;
                const isReserved = slot.reserved;
                const isAvailable = !isBlocked && !isBooked && !isReserved;

                // Debug logging
                if (isBlocked) {
                  console.log(`Rendering blocked slot ${idx}:`, {
                    startTime: slot.startTime,
                    endTime: slot.endTime,
                    blocked: slot.blocked,
                    blockedReason: slot.blockedReason,
                    hasReason: !!slot.blockedReason,
                  });
                }

                let bgColor, borderColor, statusText, statusBadgeColor;

                if (isBlocked) {
                  bgColor = "bg-red-100";
                  borderColor = "border-red-400";
                  statusText = "Blocked";
                  statusBadgeColor = "bg-red-500 text-white";
                } else if (isBooked) {
                  bgColor = "bg-gray-100";
                  borderColor = "border-gray-400";
                  statusText = "Booked";
                  statusBadgeColor = "bg-gray-500 text-white";
                } else if (isReserved) {
                  bgColor = "bg-yellow-100";
                  borderColor = "border-yellow-400";
                  statusText = "Reserved";
                  statusBadgeColor = "bg-yellow-500 text-white";
                } else {
                  bgColor = "bg-green-50";
                  borderColor = "border-green-300";
                  statusText = "Available";
                  statusBadgeColor = "bg-green-500 text-white";
                }

                return (
                  <div
                    key={idx}
                    className={`p-3 sm:p-4 rounded-lg border shadow transition duration-150 ${bgColor} ${borderColor} ${
                      isAvailable ? "hover:shadow-md cursor-pointer" : ""
                    }`}
                    onClick={isAvailable ? () => toggleExpand(idx) : undefined}
                  >
                    <div className="flex items-start justify-between mb-2 gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-base sm:text-lg font-medium text-gray-800 mb-1 flex items-center gap-2">
                          <svg
                            className="w-4 h-4 text-gray-500 flex-shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          <span className="truncate">
                            {moment(slot.startTime, "HH:mm").format("hh:mm A")}{" "}
                            – {moment(slot.endTime, "HH:mm").format("hh:mm A")}
                          </span>
                        </div>
                        {slot.tokenNumber && (
                          <div className="text-xs sm:text-sm text-gray-600 ml-6">
                            Token #{" "}
                            <span className="font-semibold">
                              {slot.tokenNumber}
                            </span>
                          </div>
                        )}
                      </div>
                      <span
                        className={`text-xs px-2 py-1 rounded-full font-medium whitespace-nowrap flex-shrink-0 ${statusBadgeColor}`}
                      >
                        {statusText}
                      </span>
                    </div>

                    {isBlocked && (
                      <div className="mt-2 p-2 sm:p-3 bg-red-50 border border-red-200 rounded-lg">
                        <div className="text-xs sm:text-sm text-red-700 font-medium mb-1">
                          🚫 This slot is blocked
                        </div>
                        {slot.blockedReason && (
                          <div className="text-xs text-red-600">
                            <strong>Reason:</strong> {slot.blockedReason}
                          </div>
                        )}
                      </div>
                    )}

                    {(slot.booked || slot.reserved) && !isBlocked ? (
                      <div className="mt-2 text-xs sm:text-sm font-medium text-gray-800">
                        {slot.booked && slot.patient && (
                          <div className="mt-1 bg-gray-50 p-2 rounded border border-gray-200">
                            <div className="flex items-center gap-1 mb-1">
                              <svg
                                className="w-3 h-3 text-gray-500"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                />
                              </svg>
                              <span className="font-semibold text-gray-800">
                                {slot.patient.name}
                              </span>
                            </div>
                            <div className="text-xs text-gray-600 space-y-0.5">
                              <div>📞 {slot.patient.phoneNumber}</div>
                              <div>🗓️ {slot.patient.age} yrs</div>
                            </div>
                          </div>
                        )}

                        {slot.reserved && (
                          <div className="mt-3">
                            <button
                              onClick={() => handleUnreserveSlot(slot)}
                              className="w-full sm:w-auto bg-red-500 text-white text-xs sm:text-sm py-2 sm:py-1.5 px-4 sm:px-3 rounded hover:bg-red-600 active:bg-red-700 transition font-medium touch-manipulation"
                            >
                              🔓 Unreserve Slot
                            </button>
                          </div>
                        )}
                      </div>
                    ) : !isBlocked ? (
                      <>
                        <button
                          onClick={() => toggleExpand(idx)}
                          className="mt-2 w-full text-xs sm:text-sm flex items-center justify-center gap-1 text-blue-600 border border-blue-500 px-3 py-2 sm:py-1.5 rounded hover:bg-blue-50 active:bg-blue-100 transition font-medium touch-manipulation"
                        >
                          {expandedSlotIndex === idx ? (
                            <>
                              <span>Hide Options</span>
                              <svg
                                className="w-4 h-4 transform rotate-180 transition-transform"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth={2}
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M19 9l-7 7-7-7"
                                />
                              </svg>
                            </>
                          ) : (
                            <>
                              <span>View Options</span>
                              <svg
                                className="w-4 h-4 transition-transform"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth={2}
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M19 9l-7 7-7-7"
                                />
                              </svg>
                            </>
                          )}
                        </button>

                        {expandedSlotIndex === idx && (
                          <div className="mt-3 flex flex-col sm:flex-row gap-2">
                            <button
                              onClick={() => handleSlotClick(slot)}
                              className="flex-1 bg-blue-600 text-white text-xs sm:text-sm py-2.5 sm:py-2 px-4 rounded hover:bg-blue-700 active:bg-blue-800 transition font-medium touch-manipulation"
                            >
                              📅 Book Slot
                            </button>
                            <button
                              onClick={() => handleReserveSlot(slot)}
                              className="flex-1 bg-yellow-500 text-white text-xs sm:text-sm py-2.5 sm:py-2 px-4 rounded hover:bg-yellow-600 active:bg-yellow-700 transition font-medium touch-manipulation"
                            >
                              🔒 Reserve Slot
                            </button>
                          </div>
                        )}
                      </>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default DoctorSlotsPage;
